import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Flame } from "lucide-react";
import { useLang } from "../lib/i18n";
import MoodDashboard from "../components/MoodDashboard";
import { track } from "../lib/ga";

const MOOD_EMOJIS = ["😞", "😐", "🙂", "😊", "🤩"];
const EMOTIONS = ["joy", "calm", "confidence", "anger", "anxiety", "fear", "sadness"];

function MoodPicker({ label, value, onChange, testIdPrefix, labels }) {
  return (
    <div data-testid={`${testIdPrefix}-picker`}>
      <div className="text-sm font-semibold text-[#3D405B] mb-2">{label}</div>
      <div className="flex flex-wrap gap-2">
        {MOOD_EMOJIS.map((e, i) => {
          const active = value === i + 1;
          return (
            <button
              key={i}
              type="button"
              onClick={() => onChange(i + 1)}
              data-testid={`${testIdPrefix}-${i + 1}`}
              className={`flex flex-col items-center gap-1 px-3 py-2 rounded-2xl border-2 transition-all min-w-[64px] ${
                active ? "border-[#E07A5F] bg-[#E07A5F]/10 scale-105" : "border-[#E8E6E1] bg-white hover:border-[#3D405B]"
              }`}
            >
              <span className="text-3xl leading-none">{e}</span>
              <span className="text-[10px] text-[#8A8D91] uppercase tracking-wider font-semibold">{labels[i]}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function computeStreak(entries) {
  if (!entries || entries.length === 0) return 0;
  const dates = new Set(entries.map(e => new Date(e.created_at).toDateString()));
  let streak = 0;
  const d = new Date();
  while (dates.has(d.toDateString())) { streak++; d.setDate(d.getDate() - 1); }
  return streak;
}

export default function Humeur() {
  const { t, lang } = useLang();
  const [parentMood, setParentMood] = useState(3);
  const [childMood, setChildMood] = useState(3);
  const [emotions, setEmotions] = useState([]);
  const [note, setNote] = useState("");
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dashKey, setDashKey] = useState(0);

  const load = () => api.get("/moods").then((r) => setHistory(r.data));
  useEffect(() => { load(); }, []);

  // Read child name from local storage (set by ChildProfileModal) for personalised insight
  let childName = "";
  try {
    const p = JSON.parse(localStorage.getItem("bf_child_profile") || "null");
    childName = p?.name || "";
  } catch { /* ignore */ }

  const emoLabels = [1, 2, 3, 4, 5].map((n) => t(`mood.emo.${n}`));

  const toggleEmotion = (e) => {
    setEmotions((prev) => (prev.includes(e) ? prev.filter((x) => x !== e) : [...prev, e]));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/moods", { parent_mood: parentMood, child_mood: childMood, emotions, note });
      track.moodSaved(emotions.length > 0);
      toast.success(t("mood.saved"));
      setNote("");
      setEmotions([]);
      await load();
      setDashKey((k) => k + 1);
    } finally { setBusy(false); }
  };

  const streak = computeStreak(history);
  const locale = t("locale");

  return (
    <div className="space-y-8" data-testid="humeur-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl md:text-5xl text-[#3D405B]">{t("mood.title")}</h1>
          <p className="text-[#8A8D91] mt-2 max-w-2xl">{t("mood.subtitle")}</p>
        </div>
        {streak > 0 && (
          <div className="inline-flex items-center gap-2 bg-[#E07A5F] text-white rounded-full px-4 py-2 shadow-sm" data-testid="mood-streak">
            <Flame className="w-4 h-4" /> {t(streak === 1 ? "mood.streak.one" : "mood.streak", { n: streak })}
          </div>
        )}
      </header>

      <form
        onSubmit={submit}
        className="bg-white border border-[#E8E6E1] rounded-3xl p-6 md:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] space-y-6"
        data-testid="mood-form"
      >
        <MoodPicker label={t("mood.parent")} value={parentMood} onChange={setParentMood} testIdPrefix="parent-mood" labels={emoLabels} />
        <MoodPicker label={t("mood.child")} value={childMood} onChange={setChildMood} testIdPrefix="child-mood" labels={emoLabels} />

        <div data-testid="mood-emotions">
          <div className="text-sm font-semibold text-[#3D405B] mb-2">{t("mood.emotions.label")}</div>
          <div className="flex flex-wrap gap-2">
            {EMOTIONS.map((e) => {
              const active = emotions.includes(e);
              return (
                <button
                  key={e}
                  type="button"
                  onClick={() => toggleEmotion(e)}
                  data-testid={`mood-emotion-${e}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    active
                      ? "bg-[#3D405B] text-white border-[#3D405B]"
                      : "bg-white text-[#3D405B] border-[#E8E6E1] hover:border-[#3D405B]"
                  }`}
                >
                  {t(`emo.${e}`)}
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-[#8A8D91] mt-2">{t("mood.emotions.hint")}</p>
        </div>

        <div>
          <label className="text-sm font-semibold text-[#3D405B] mb-2 block">{t("mood.note.label")}</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder={t("mood.note.placeholder")}
            data-testid="mood-note"
            className="w-full bg-[#FDFBF7] border border-[#E8E6E1] rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#81B29A]/50 focus:border-[#81B29A] text-[#3D405B]"
          />
        </div>
        <button
          type="submit"
          disabled={busy}
          data-testid="mood-submit"
          className="bg-[#E07A5F] text-white rounded-full px-6 py-3 font-medium hover:bg-[#D56D52] transition-colors disabled:opacity-50"
        >
          {busy ? t("mood.submitting") : t("mood.submit")}
        </button>
      </form>

      <MoodDashboard key={dashKey} childName={childName} />

      <section data-testid="mood-history">
        <h2 className="font-heading text-2xl md:text-3xl text-[#3D405B] mb-4">{t("mood.history")}</h2>
        {history.length === 0 ? (
          <p className="text-[#8A8D91] italic">{t("mood.empty")}</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {history.map((h) => (
              <article key={h.id} className="bg-white border border-[#E8E6E1] rounded-2xl p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)]" data-testid={`mood-entry-${h.id}`}>
                <div className="text-xs text-[#8A8D91] uppercase tracking-widest">
                  {new Date(h.created_at).toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long" })}
                </div>
                <div className="flex items-center gap-4 mt-2">
                  <div className="text-center">
                    <div className="text-3xl">{MOOD_EMOJIS[h.parent_mood - 1]}</div>
                    <div className="text-[10px] text-[#8A8D91] uppercase">{t("mood.parent.label")}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl">{MOOD_EMOJIS[h.child_mood - 1]}</div>
                    <div className="text-[10px] text-[#8A8D91] uppercase">{t("mood.child.label")}</div>
                  </div>
                  {h.note && <p className="text-sm text-[#3D405B]/85 flex-1 leading-relaxed">{h.note}</p>}
                </div>
                {h.emotions && h.emotions.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {h.emotions.map((e) => (
                      <span key={e} className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-[#F4F1EA] text-[#3D405B] border border-[#E8E6E1]">
                        {t(`emo.${e}`)}
                      </span>
                    ))}
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
